import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import { useOrderView } from '@/contexts/OrderViewContext';
import { getScheduleDate } from '@/store/selectors/schedule';

const TitleUpdater = () => {
  const location = useLocation();
  const intl = useIntl();
  const { activeOrder } = useOrderView();
  const scheduleDate = useSelector(getScheduleDate);
  const filterQuery = useSelector(state => state.filter?.query);

  useEffect(() => {
    const { pathname, search } = location;
    let titleKey = '';
    let prefix = '';
    let translatedTitle = '';

    // 1. Specific matching routes
    if (pathname === '/access-denied') {
      titleKey = 'ACCESS_DENIED';
    } else if (pathname.startsWith('/new-client')) {
      titleKey = 'DEFAULT';
    } else if (pathname.startsWith('/marketplace')) {
      if (pathname.includes('/enabled')) {
        titleKey = 'PAGE_TITLE.ACTIVE_INTEGRATIONS';
      } else {
        titleKey = 'PAGE_TITLE.INTEGRATIONS';
      }
    } else if (pathname.startsWith('/commercial')) {
      if (pathname.includes('/settings')) {
        titleKey = 'USERS';
      } else {
        titleKey = 'COMMERCIAL';
      }
    } else if (pathname.startsWith('/inspections')) {
      titleKey = 'PAGE_TITLE.ORDERS_INDEPENDENT';
    } else if (pathname.startsWith('/orders')) {
      const searchParams = new URLSearchParams(search);
      const tab = searchParams.get('tab');

      if (pathname.endsWith('/cancel')) {
        titleKey = 'BUTTON.CANCEL_ORDER';
      } else if (pathname.includes('/new')) {
        titleKey = 'orderForm.newOrder';
      } else if (pathname.includes('/previous-inspection/edit')) {
        titleKey = 'orderForm.editOrder';
      } else if (pathname.includes('/independent-assign')) {
        titleKey = 'BUTTON.ASSIGN_ORDER';
      } else if (pathname.includes('/merge')) {
        titleKey = 'BUTTON.MERGE_ORDER';
      } else if (pathname.includes('/assign')) {
        titleKey = 'BUTTON.ASSIGN_INSPECTOR';
      } else if (pathname.includes('/performing-inspector')) {
        titleKey = 'PERFORMING_INSPECTORS';
      } else if (pathname.includes('/extra-actions')) {
        titleKey = 'BUTTON.EXTRA_ACTIONS';
      } else if (pathname.includes('/checklist')) {
        titleKey = 'checklist';
      } else if (pathname.includes('/repair-budget')) {
        titleKey = 'REPAIR_BUDGET';
      } else if (pathname.endsWith('/edit')) {
        titleKey = 'orderForm.editOrder';
      } else if (pathname.includes('/financial-extract')) {
        titleKey = 'EXTRACT';
      } else if (pathname.includes('/dashboards')) {
        titleKey = 'DASHBOARD';
      } else if (filterQuery?.text && String(filterQuery.text).trim() !== '') {
        titleKey = 'PAGE_TITLE.ORDERS';
      } else if (tab) {
        const tabKeys = [
          'waiting',
          'accepted',
          'assigned',
          'in_progress',
          'finalized',
          'archived',
          'vnr',
          'sent',
          'rejected',
          'canceled',
        ];
        if (tabKeys.includes(tab)) {
          titleKey = `PAGE_TITLE.ORDERS_${tab.toUpperCase()}`;
        } else {
          titleKey = `PAGE_TITLE.ORDERS_WAITING`;
        }
      } else {
        const parts = pathname.split('/');
        const lastPart = parts[parts.length - 1];
        const isDetails = parts.length === 3 && lastPart !== 'orders' && lastPart !== 'selection';

        if (isDetails) {
          titleKey = 'DEFAULT';
          const code = activeOrder?.code || '';
          const identifier = activeOrder?.form?.details?.building_id || activeOrder?.identifier || '';
          if (code && identifier) {
            translatedTitle = `${code} - ${identifier}`;
          } else if (code) {
            translatedTitle = code;
          } else if (identifier) {
            translatedTitle = identifier;
          } else {
            translatedTitle = 'Pedido';
          }
        } else {
          const me = JSON.parse(window.sessionStorage.getItem('me'));
          if (me?.authRole === 'ROLE_CLIENT') {
            titleKey = 'MY_INSPECTIONS';
          } else {
            titleKey = 'PAGE_TITLE.ORDERS_WAITING';
          }
        }
      }
    } else if (pathname.startsWith('/clients')) {
      if (pathname.includes('/new')) {
        titleKey = 'PAGE_TITLE.NEW_CUSTOMER';
      } else if (pathname.includes('/enabled')) {
        titleKey = 'PAGE_TITLE.CLIENTS_ACTIVE';
      } else if (pathname.includes('/disabled')) {
        titleKey = 'PAGE_TITLE.CLIENTS_INACTIVE';
      } else {
        titleKey = 'CUSTOMERS';
      }
    } else if (pathname.startsWith('/inspectors')) {
      if (pathname.includes('/new')) {
        titleKey = 'ADD_INSPECTOR';
      } else if (pathname.includes('/active')) {
        titleKey = 'PAGE_TITLE.INSPECTORS_ACTIVE';
      } else if (pathname.includes('/deleted')) {
        titleKey = 'PAGE_TITLE.INSPECTORS_INACTIVE';
      } else {
        titleKey = 'INSPECTORS';
      }
    } else if (pathname.startsWith('/schedule')) {
      titleKey = 'LeftDrawer.schedule';
    } else if (pathname.startsWith('/settings')) {
      titleKey = 'USERS';
    }

    if (titleKey && titleKey !== 'DEFAULT') {
      try {
        if (intl.messages[titleKey]) {
          translatedTitle = intl.formatMessage({ id: titleKey });
        } else if (titleKey === 'ACCESS_DENIED') {
          translatedTitle = intl.locale.startsWith('es') ? 'Acceso denegado' : 'Acesso Negado';
        } else if (titleKey === 'checklist') {
          translatedTitle = 'Checklist';
        } else if (titleKey === 'ADD_INSPECTOR') {
          translatedTitle = intl.locale.startsWith('es') ? 'Añadir inspector' : 'Adicionar vistoriador';
        }
      } catch (e) {
        console.debug('Error formatting title: ', e);
      }
    }

    if (pathname.startsWith('/schedule') && scheduleDate) {
      try {
        const dateStr = format(scheduleDate, 'dd/MM/yyyy');
        if (translatedTitle) {
          translatedTitle = `${translatedTitle} - ${dateStr}`;
        }
      } catch (e) {
        console.debug('Error formatting schedule date: ', e);
      }
    }

    const appName = 'Rede Vistorias';
    if (translatedTitle) {
      document.title = `${prefix}${translatedTitle} | ${appName}`;
    } else {
      document.title = appName;
    }
  }, [location, intl, activeOrder, scheduleDate, filterQuery]);

  return null;
};

export default TitleUpdater;
